import { useEffect, useState } from "react";
import type React from "react";

export type CourseSelectorCourse = {
  id: string;
  slug: string;
  title: string;
};

type CourseSelectorProps = {
  courses: CourseSelectorCourse[];
  onSelectCourse: (courseId: string, courseSlug: string) => void;
  lastActiveCourseId?: string | null;
};

export default function CourseSelector({ courses, onSelectCourse, lastActiveCourseId }: CourseSelectorProps) {
  const [lastOpenedCourseId, setLastOpenedCourseId] = useState<string | null>(null);
  const [lastOpenedCourseSlug, setLastOpenedCourseSlug] = useState<string | null>(null);

  useEffect(() => {
    try {
      setLastOpenedCourseId(localStorage.getItem("scico_last_course_id"));
      setLastOpenedCourseSlug(localStorage.getItem("scico_last_course_slug"));
    } catch {
      // ignore
    }
  }, []);

  const handleSelectCourse = (courseId: string, courseSlug: string) => {
    try {
      localStorage.setItem("scico_last_course_id", courseId);
      localStorage.setItem("scico_last_course_slug", courseSlug);
    } catch {
      // ignore
    }
    setLastOpenedCourseId(courseId);
    setLastOpenedCourseSlug(courseSlug);
    onSelectCourse(courseId, courseSlug);
  };

  const categories = courses.map((course) => {
    const { color, Illustration } = getCourseVisual(course.title);
    return {
      id: course.id,
      slug: course.slug,
      title: course.title,
      color,
      illustration: <Illustration />,
    };
  });

  return (
    <>
      <style>{`
        @keyframes swing {
          0%, 100% { transform: rotate(-18deg); }
          50% { transform: rotate(18deg); }
        }
        @keyframes orbit1 {
          from { transform: rotate(0deg) translateX(65px) rotate(0deg); }
          to   { transform: rotate(360deg) translateX(65px) rotate(-360deg); }
        }
        @keyframes orbit2 {
          from { transform: rotate(60deg) translateX(65px) rotate(-60deg); }
          to   { transform: rotate(420deg) translateX(65px) rotate(-420deg); }
        }
        @keyframes orbit3 {
          from { transform: rotate(120deg) translateX(65px) rotate(-120deg); }
          to   { transform: rotate(480deg) translateX(65px) rotate(-480deg); }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.85; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(0.6); }
        }
        @keyframes moonGlow {
          0%, 100% { opacity: 0.92; }
          50% { opacity: 0.75; }
        }
        @keyframes pulse-star {
          0%, 100% { r: 28; opacity: 0.95; }
          50% { r: 34; opacity: 0.8; }
        }
        @keyframes spin-disk {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes expand-ring {
          0%   { r: 0; opacity: 0.5; }
          100% { r: 140; opacity: 0; }
        }
        @keyframes float-apple {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(10px); }
        }
        @keyframes telescope-scan {
          0%, 100% { transform: translate(150px,380px) rotate(-5deg); }
          50% { transform: translate(150px,380px) rotate(5deg); }
        }
        @keyframes beam-pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes cosmic-expand {
          0% { transform: scale(0.95); opacity: 0.2; }
          100% { transform: scale(1.1); opacity: 0; }
        }
        @keyframes wave-shift {
          from { transform: translateX(0); }
          to   { transform: translateX(-70px); }
        }
        @keyframes wave-ride {
          0%   { transform: translate(10px,   0px); }
          12%  { transform: translate(27px, -35px); }
          25%  { transform: translate(70px,   0px); }
          37%  { transform: translate(113px, 35px); }
          50%  { transform: translate(150px,  0px); }
          62%  { transform: translate(187px,-35px); }
          75%  { transform: translate(220px,  0px); }
          87%  { transform: translate(257px, 35px); }
          100% { transform: translate(290px,  0px); }
        }

        .pendulum-group {
          transform-origin: 150px 40px;
          animation: swing 2.4s ease-in-out infinite;
        }
        .electron-1 {
          transform-box: fill-box;
          transform-origin: center;
          animation: orbit1 2s linear infinite;
        }
        .electron-2 {
          transform-box: fill-box;
          transform-origin: center;
          animation: orbit2 2.8s linear infinite;
        }
        .electron-3 {
          transform-box: fill-box;
          transform-origin: center;
          animation: orbit3 3.5s linear infinite;
        }
        .star-twinkle { animation: twinkle var(--d, 1.8s) ease-in-out infinite var(--delay, 0s); }
        .moon-glow { animation: moonGlow 3s ease-in-out infinite; }
        .supernova-core { animation: pulse-star 1.8s ease-in-out infinite; }
        .expand-ring-anim {
          animation: expand-ring 3s ease-out infinite;
        }
        .apple-float {
          animation: float-apple 2s ease-in-out infinite;
        }
        .telescope-group {
          animation: telescope-scan 4s ease-in-out infinite;
          transform-origin: 150px 380px;
        }
        .beam-anim { animation: beam-pulse 1.2s ease-in-out infinite; }

        .col-card {
          cursor: pointer;
          transition: filter 0.3s ease, transform 0.3s ease;
          border-radius: 1rem;
          min-height: 300px;
        }
        .col-card:hover {
          filter: brightness(1.06);
          transform: translateY(-4px);
        }
        .fact-bubble {
          animation: fadeSlideUp 0.35s cubic-bezier(0.4,0,0.2,1) forwards;
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .col-title {
          transition: transform 0.3s ease, font-size 0.3s ease;
        }
        .col-card:hover .col-title {
          transform: scale(1.08);
        }

        .hint-label {
          transition: opacity 0.3s;
        }
        .col-card:hover .hint-label {
          opacity: 0;
        }

      `}</style>
      <div className="w-full flex flex-col p-4 md:p-8 max-w-7xl mx-auto pb-24 pt-12">
        <h1 className="text-3xl font-bold mb-8 text-center bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Choose Your Subject</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat) => {
            const shouldShowContinue =
              cat.id === lastActiveCourseId ||
              cat.id === lastOpenedCourseId ||
              (!!lastOpenedCourseSlug && cat.slug === lastOpenedCourseSlug);

            return (
              <div
                key={cat.id}
                className={`col-card flex flex-col items-center relative overflow-hidden${shouldShowContinue ? " ring-2 ring-white/40 ring-inset" : ""}`}
                style={{ backgroundColor: cat.color }}
                onClick={() => handleSelectCourse(cat.id, cat.slug)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelectCourse(cat.id, cat.slug);
                  }
                }}
              >
                <div className="pt-8 px-4 text-center z-10">
                  <h2
                    className="col-title text-white font-light text-xl leading-tight tracking-wide"
                    style={{
                      fontFamily: "'Nunito', 'Quicksand', sans-serif",
                      textShadow: "0 1px 3px rgba(0,0,0,0.15)",
                      whiteSpace: "pre-line",
                      display: "inline-block",
                    }}
                  >
                    {cat.title}
                  </h2>
                </div>

                <div className="flex-1 flex items-end justify-center w-full pb-0">{cat.illustration}</div>

                {shouldShowContinue ? (
                  <div className="hint-label absolute bottom-10 left-0 right-0 text-center" style={{ pointerEvents: "none" }}>
                    <span className="inline-flex items-center rounded-full bg-white/20 backdrop-blur-sm px-3 py-1 text-xs font-semibold text-white border border-white/30">
                      Continue →
                    </span>
                  </div>
                ) : (
                  <div className="hint-label absolute bottom-12 left-0 right-0 text-center" style={{ pointerEvents: "none" }}>
                    <span className="text-white/50 text-xs tracking-widest uppercase">tap to start</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function getCourseVisual(title: string): { color: string; Illustration: () => JSX.Element } {
  const normalizedTitle = title.toLowerCase();

  if (normalizedTitle.includes("astrophysics")) {
    return { color: "#F5A623", Illustration: AstrophysicsIllustration };
  }
  if (normalizedTitle.includes("astronomy")) {
    return { color: "#3BBFB8", Illustration: AstronomyIllustration };
  }
  if (normalizedTitle.includes("quantum") || normalizedTitle.includes("quantom")) {
    return { color: "#7BC843", Illustration: QuantumMechanicsIllustration };
  }
  if (normalizedTitle.includes("physics")) {
    return { color: "#E8527A", Illustration: PhysicsIllustration };
  }

  return { color: "#7BC843", Illustration: AstronomyIllustration };
}

function PhysicsIllustration() {
  return (
    <svg
      viewBox="0 0 300 520"
      className="w-full h-full"
      style={{ maxHeight: "85vh" }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <marker id="arrow1" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill="rgba(255,255,255,0.6)" />
        </marker>
      </defs>

      {/* Pendulum group — swings from pivot */}
      <g className="pendulum-group">
        <line x1="150" y1="40" x2="150" y2="180" stroke="rgba(255,255,255,0.7)" strokeWidth="3" strokeLinecap="round" />
        <circle cx="150" cy="210" r="32" fill="white" opacity="0.95" />
        <circle cx="150" cy="210" r="22" fill="#E8527A" opacity="0.6" />
        <circle cx="150" cy="210" r="12" fill="white" opacity="0.9" />
      </g>

      {/* Motion arcs */}
      <path d="M80 170 Q150 250 220 170" stroke="rgba(255,255,255,0.35)" strokeWidth="2" fill="none" strokeDasharray="6 4" />

      {/* Apple with float animation */}
      <g className="apple-float">
        <ellipse cx="60" cy="300" rx="18" ry="20" fill="#5ED35E" />
        <ellipse cx="60" cy="291" rx="7" ry="4" fill="#4AB94A" />
        <path d="M60 282 Q68 275 65 265" stroke="#5A3A1A" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <ellipse cx="53" cy="296" rx="5" ry="7" fill="white" opacity="0.25" transform="rotate(-20,53,296)" />
      </g>
      <line x1="60" y1="325" x2="60" y2="360" stroke="rgba(255,255,255,0.6)" strokeWidth="2.5" strokeLinecap="round" markerEnd="url(#arrow1)" />

      {/* Atom */}
      <g transform="translate(150,390)">
        <circle cx="0" cy="0" r="18" fill="white" opacity="0.95" />
        <circle cx="0" cy="0" r="11" fill="#F08" opacity="0.5" />
        <circle cx="0" cy="0" r="5" fill="white" opacity="0.9" />
        <ellipse cx="0" cy="0" rx="65" ry="22" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2" transform="rotate(0)" />
        <ellipse cx="0" cy="0" rx="65" ry="22" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2" transform="rotate(60)" />
        <ellipse cx="0" cy="0" rx="65" ry="22" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2" transform="rotate(120)" />
        <circle className="electron-1" cx="65" cy="0" r="8" fill="white" opacity="0.9" />
        <circle className="electron-2" cx="65" cy="0" r="8" fill="white" opacity="0.9" transform="rotate(60)" />
        <circle className="electron-3" cx="65" cy="0" r="8" fill="white" opacity="0.9" transform="rotate(120)" />
      </g>

      {/* Magnet */}
      <g transform="translate(230,290)">
        <path d="M-12,-40 L-12,0 Q-12,22 0,22 Q12,22 12,0 L12,-40" fill="none" stroke="white" strokeWidth="14" strokeLinecap="round" opacity="0.9" />
        <path d="M-12,-40 L-12,0 Q-12,22 0,22 Q12,22 12,0 L12,-40" fill="none" stroke="#E8527A" strokeWidth="7" strokeLinecap="round" opacity="0.7" />
        <rect x="-20" y="-55" width="16" height="18" rx="3" fill="#CC3366" />
        <rect x="4" y="-55" width="16" height="18" rx="3" fill="#4A90D9" />
        <path d="M-22,-20 Q-40,-5 -22,12" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" fill="none" strokeDasharray="4 3" />
        <path d="M22,-20 Q40,-5 22,12" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" fill="none" strokeDasharray="4 3" />
      </g>

      <path d="M0,490 Q37.5,470 75,490 Q112.5,510 150,490 Q187.5,470 225,490 Q262.5,510 300,490 L300,520 L0,520 Z" fill="rgba(255,255,255,0.18)" />
    </svg>
  );
}

function AstronomyIllustration() {
  const stars = [
    [40, 60, 4, "1.8s", "0s"],
    [260, 80, 3, "2.2s", "0.4s"],
    [200, 50, 5, "1.5s", "0.8s"],
    [100, 100, 3, "2.6s", "0.2s"],
    [270, 140, 4, "1.9s", "1.0s"],
    [30, 150, 3, "2.1s", "0.6s"],
    [240, 200, 4, "1.7s", "1.3s"],
    [80, 200, 3, "2.4s", "0.3s"],
  ];

  return (
    <svg
      viewBox="0 0 300 520"
      className="w-full h-full"
      style={{ maxHeight: "85vh" }}
      xmlns="http://www.w3.org/2000/svg"
    >
      {stars.map(([cx, cy, r, d, delay], i) => (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={r}
          fill="white"
          className="star-twinkle"
          style={{ "--d": d, "--delay": delay } as React.CSSProperties}
        />
      ))}

      {/* Moon with glow */}
      <g className="moon-glow">
        <circle cx="200" cy="170" r="55" fill="white" opacity="0.92" />
        <circle cx="182" cy="155" r="9" fill="rgba(200,200,200,0.6)" />
        <circle cx="220" cy="180" r="13" fill="rgba(200,200,200,0.5)" />
        <circle cx="195" cy="195" r="6" fill="rgba(200,200,200,0.55)" />
        <circle cx="210" cy="140" r="5" fill="rgba(200,200,200,0.5)" />
        <circle cx="180" cy="170" r="55" fill="#3BBFB8" opacity="0.35" />
      </g>

      {/* Telescope group with scan animation */}
      <g className="telescope-group">
        <line x1="0" y1="60" x2="-60" y2="130" stroke="rgba(255,255,255,0.8)" strokeWidth="5" strokeLinecap="round" />
        <line x1="0" y1="60" x2="60" y2="130" stroke="rgba(255,255,255,0.8)" strokeWidth="5" strokeLinecap="round" />
        <line x1="0" y1="60" x2="0" y2="130" stroke="rgba(255,255,255,0.8)" strokeWidth="4" strokeLinecap="round" />
        <line x1="-45" y1="105" x2="45" y2="105" stroke="rgba(255,255,255,0.5)" strokeWidth="3" strokeLinecap="round" />
        <circle cx="0" cy="55" r="12" fill="white" opacity="0.9" />
        <rect x="-16" y="-65" width="32" height="105" rx="16" fill="white" opacity="0.9" transform="rotate(-30)" />
        <rect x="-16" y="-65" width="32" height="105" rx="16" fill="#3BBFB8" opacity="0.4" transform="rotate(-30)" />
        <rect x="22" y="-82" width="20" height="14" rx="5" fill="rgba(255,255,255,0.7)" transform="rotate(-30, 22, -82)" />
        <circle cx="-48" cy="36" r="17" fill="rgba(255,255,255,0.85)" />
        <circle cx="-48" cy="36" r="12" fill="#3BBFB8" opacity="0.6" />
        <circle cx="-48" cy="36" r="6" fill="white" opacity="0.95" />
      </g>

      {/* Constellation */}
      <polyline points="50,240 80,260 70,290 95,310" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" strokeDasharray="4 3" />
      {[[50,240],[80,260],[70,290],[95,310]].map(([cx,cy],j) => (
        <circle key={j} cx={cx} cy={cy} r="4" fill="white" opacity="0.8" />
      ))}

      {/* Saturn */}
      <g transform="translate(80, 330)">
        <ellipse cx="0" cy="0" rx="48" ry="12" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="3" />
        <circle cx="0" cy="0" r="28" fill="white" opacity="0.88" />
        <circle cx="0" cy="0" r="28" fill="#F5A623" opacity="0.25" />
        <ellipse cx="0" cy="0" rx="48" ry="12" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="8" />
        <ellipse cx="0" cy="0" rx="48" ry="12" fill="none" stroke="#3BBFB8" strokeWidth="5" strokeDasharray="28 68" strokeDashoffset="-14" />
        <circle cx="0" cy="0" r="22" fill="white" opacity="0.88" />
        <circle cx="0" cy="0" r="22" fill="#F5A623" opacity="0.3" />
      </g>

      <path d="M0,490 Q37.5,470 75,490 Q112.5,510 150,490 Q187.5,470 225,490 Q262.5,510 300,490 L300,520 L0,520 Z" fill="rgba(255,255,255,0.15)" />
    </svg>
  );
}

function AstrophysicsIllustration() {
  return (
    <svg
      viewBox="0 0 300 520"
      className="w-full h-full"
      style={{ maxHeight: "85vh" }}
      xmlns="http://www.w3.org/2000/svg"
    >
      {[[20,80,3],[280,60,2],[260,110,4],[40,200,3],[270,250,3]].map(([cx,cy,r],i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill="white" opacity="0.7"
          className="star-twinkle"
          style={{ "--d": `${1.5 + i*0.3}s`, "--delay": `${i*0.2}s` } as React.CSSProperties}
        />
      ))}

      {/* Star burst */}
      <g transform="translate(150, 160)">
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i * 30 * Math.PI) / 180;
          const x2 = Math.cos(angle) * 90;
          const y2 = Math.sin(angle) * 90;
          return (
            <line key={i} x1="0" y1="0" x2={x2} y2={y2}
              stroke="rgba(255,255,255,0.55)"
              strokeWidth={i % 3 === 0 ? 3 : 1.5}
              strokeLinecap="round"
            />
          );
        })}
        <circle cx="0" cy="0" r="60" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="18" />
        <circle cx="0" cy="0" r="40" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="10" />
        <circle cx="0" cy="0" r="20" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="6" />
        <circle className="supernova-core" cx="0" cy="0" r="28" fill="white" opacity="0.95" />
        <circle cx="0" cy="0" r="18" fill="#FFF8E1" opacity="0.9" />
        <circle cx="0" cy="0" r="10" fill="#FFD740" opacity="0.9" />
      </g>

      {/* Spectral lines */}
      <g transform="translate(30, 290)">
        <rect x="0" y="0" width="240" height="50" rx="6" fill="rgba(255,255,255,0.12)" />
        {[
          ["#FF4444", 20], ["#FF8800", 45], ["#FFFF00", 70], ["#44FF44", 100],
          ["#00AAFF", 130], ["#0044FF", 160], ["#8800FF", 185], ["#FF44FF", 210],
        ].map(([color, x], i) => (
          <rect key={i} x={x} y="5" width="8" height="40" rx="2" fill={color as string} opacity="0.85" />
        ))}
        <text x="120" y="68" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="11" fontFamily="sans-serif">Spectral Lines</text>
      </g>

      {/* Pulsar with beam */}
      <g transform="translate(80, 410)">
        <circle cx="0" cy="0" r="22" fill="white" opacity="0.9" />
        <circle cx="0" cy="0" r="14" fill="#F5A623" opacity="0.5" />
        <circle cx="0" cy="0" r="7" fill="white" opacity="0.95" />
        <line className="beam-anim" x1="0" y1="-22" x2="0" y2="-65" stroke="rgba(255,255,255,0.7)" strokeWidth="7" strokeLinecap="round" />
        <line className="beam-anim" x1="0" y1="22" x2="0" y2="65" stroke="rgba(255,255,255,0.7)" strokeWidth="7" strokeLinecap="round" />
        <ellipse cx="0" cy="-55" rx="13" ry="5" fill="rgba(255,255,255,0.4)" />
        <ellipse cx="0" cy="55" rx="13" ry="5" fill="rgba(255,255,255,0.4)" />
        <text x="0" y="88" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="11" fontFamily="sans-serif">Pulsar</text>
      </g>

      {/* Black hole */}
      <g transform="translate(220, 410)">
        <ellipse cx="0" cy="0" rx="55" ry="15" fill="none" stroke="rgba(255,200,50,0.6)" strokeWidth="10" />
        <ellipse cx="0" cy="0" rx="55" ry="15" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
        <circle cx="0" cy="0" r="26" fill="#1a0a00" />
        <circle cx="0" cy="0" r="26" stroke="rgba(255,200,50,0.7)" strokeWidth="3" fill="none" />
        <circle cx="0" cy="0" r="32" fill="none" stroke="rgba(255,200,50,0.25)" strokeWidth="8" />
        <text x="0" y="52" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="11" fontFamily="sans-serif">Black Hole</text>
      </g>

      <path d="M0,495 Q37.5,475 75,495 Q112.5,515 150,495 Q187.5,475 225,495 Q262.5,515 300,495 L300,520 L0,520 Z" fill="rgba(255,255,255,0.15)" />
    </svg>
  );
}

function QuantumMechanicsIllustration() {
  return (
    <svg
      viewBox="0 0 300 520"
      className="w-full h-full"
      style={{ maxHeight: "85vh" }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <marker id="qarrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="rgba(255,255,255,0.6)" />
        </marker>
      </defs>

      {/* Wave-particle duality: probability wave */}
      <g transform="translate(0, 80)">
        <path
          d="M10,0 C30,-35 50,-35 70,0 C90,35 110,35 130,0 C150,-35 170,-35 190,0 C210,35 230,35 250,0 C270,-35 285,-35 295,0"
          fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="3" strokeLinecap="round"
          style={{ animation: "wave-shift 2s linear infinite" }}
        />
        <path
          d="M10,0 C30,-22 50,-22 70,0 C90,22 110,22 130,0 C150,-22 170,-22 190,0 C210,22 230,22 250,0 C270,-22 285,-22 295,0"
          fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2" strokeLinecap="round"
          style={{ animation: "wave-shift 2s linear infinite 0.4s" }}
        />
        {/* Particle dot riding the wave */}
        <circle cx="0" cy="0" r="10" fill="white" opacity="0.95"
          style={{ animation: "wave-ride 2s linear infinite" }}
        />
        <text x="150" y="45" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="11" fontFamily="sans-serif">Wave-Particle Duality</text>
      </g>

      {/* Double-slit experiment */}
      <g transform="translate(0, 185)">
        {/* Incoming particle beam */}
        <line x1="10" y1="35" x2="90" y2="35" stroke="rgba(255,255,255,0.6)" strokeWidth="2.5" strokeDasharray="6 3" />
        <circle cx="10" cy="35" r="7" fill="white" opacity="0.9" />

        {/* Barrier with two slits */}
        <rect x="95" y="5" width="14" height="22" rx="3" fill="rgba(255,255,255,0.85)" />
        <rect x="95" y="35" width="14" height="10" rx="2" fill="rgba(255,255,255,0.3)" />
        <rect x="95" y="53" width="14" height="22" rx="3" fill="rgba(255,255,255,0.85)" />

        {/* Interference fan lines through slits */}
        {[-28,-18,-8,0,8,18,28].map((dy, i) => (
          <line key={i}
            x1="109" y1="35"
            x2="280" y2={35 + dy * 3.2}
            stroke="rgba(255,255,255,0.18)" strokeWidth="1.2"
          />
        ))}
        {[-28,-18,-8,0,8,18,28].map((dy, i) => (
          <line key={`b${i}`}
            x1="109" y1="45"
            x2="280" y2={45 + dy * 3.2}
            stroke="rgba(255,255,255,0.18)" strokeWidth="1.2"
          />
        ))}

        {/* Interference pattern on screen */}
        <rect x="278" y="0" width="10" height="75" rx="3" fill="rgba(255,255,255,0.15)" />
        {[5,17,29,41,53,65].map((y, i) => (
          <rect key={i} x="278" y={y} width="10" height="8" rx="2"
            fill="white" opacity={[0.4,0.8,1,0.9,0.75,0.35][i]}
          />
        ))}
        <text x="150" y="92" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="11" fontFamily="sans-serif">Double-Slit Experiment</text>
      </g>

      {/* Schrödinger's cat box */}
      <g transform="translate(30, 320)">
        {/* Box body */}
        <rect x="0" y="0" width="110" height="85" rx="10" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.7)" strokeWidth="3" />
        {/* Box lid slightly open */}
        <path d="M0,0 L-8,-18 L118,-18 L110,0" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.65)" strokeWidth="2.5" strokeLinejoin="round" />

        {/* Cat silhouette */}
        <ellipse cx="55" cy="58" rx="22" ry="18" fill="rgba(255,255,255,0.6)" />
        <circle cx="55" cy="38" r="13" fill="rgba(255,255,255,0.6)" />
        {/* Cat ears */}
        <polygon points="44,28 40,15 51,26" fill="rgba(255,255,255,0.6)" />
        <polygon points="66,28 70,15 59,26" fill="rgba(255,255,255,0.6)" />
        {/* Question mark over cat — superposition */}
        <text x="55" y="47" textAnchor="middle" fill="#7BC843" fontSize="20" fontWeight="bold" fontFamily="sans-serif">?</text>

        {/* Superposition label */}
        <text x="55" y="104" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="10" fontFamily="sans-serif">Schrödinger's Cat</text>
      </g>

      {/* Uncertainty principle */}
      <g transform="translate(165, 320)">
        <rect x="0" y="0" width="110" height="85" rx="10" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
        {/* Δx blurry position */}
        <text x="55" y="28" textAnchor="middle" fill="rgba(255,255,255,0.9)" fontSize="13" fontFamily="serif" fontStyle="italic">Δx · Δp</text>
        <line x1="15" y1="38" x2="95" y2="38" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
        <text x="55" y="54" textAnchor="middle" fill="rgba(255,255,255,0.9)" fontSize="13" fontFamily="serif" fontStyle="italic">≥ ℏ/2</text>
        {/* Blurry position dots */}
        {[-18,-10,0,10,18].map((dx, i) => (
          <circle key={i} cx={55 + dx} cy="70" r={6 - Math.abs(dx) / 5}
            fill="white" opacity={0.9 - Math.abs(dx) * 0.04}
          />
        ))}
        <text x="55" y="104" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="10" fontFamily="sans-serif">Uncertainty Principle</text>
      </g>

      {/* Quantum tunneling arrow through barrier */}
      <g transform="translate(20, 435)">
        <rect x="100" y="-20" width="18" height="40" rx="4" fill="rgba(255,255,255,0.6)" />
        <circle cx="60" cy="0" r="9" fill="white" opacity="0.9" />
        <line x1="70" y1="0" x2="98" y2="0" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeDasharray="5 3" />
        <line x1="118" y1="0" x2="150" y2="0" stroke="rgba(255,255,255,0.8)" strokeWidth="2.5" markerEnd="url(#qarrow)" />
        <circle cx="160" cy="0" r="9" fill="white" opacity="0.7" />
        <text x="110" y="32" textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="10" fontFamily="sans-serif">Quantum Tunneling</text>
      </g>

      <path d="M0,495 Q37.5,475 75,495 Q112.5,515 150,495 Q187.5,475 225,495 Q262.5,515 300,495 L300,520 L0,520 Z" fill="rgba(255,255,255,0.15)" />
    </svg>
  );
}
